import React, { useState, useEffect } from "react";
import {
  ArrowRight,
  Code,
  Globe,
  Zap,
  Shield,
  Users,
  Briefcase,
  BookOpen,
  Mail,
  MapPin,
  Linkedin,
  Twitter,
  Github,
  Menu,
  X,
} from "lucide-react";
import { Button } from "../components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { toast } from "sonner";

const Home = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [activeSection, setActiveSection] = useState("home");
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    company: "",
    message: "",
  });

  useEffect(() => {
    const handleScroll = () => {
      const sections = [
        "home",
        "about",
        "services",
        "technologies",
        "careers",
        "blog",
        "contact",
      ];
      const scrollPosition = window.scrollY + 100;

      for (const section of sections) {
        const element = document.getElementById(section);
        if (element) {
          const offsetTop = element.offsetTop;
          const offsetBottom = offsetTop + element.offsetHeight;
          if (scrollPosition >= offsetTop && scrollPosition < offsetBottom) {
            setActiveSection(section);
            break;
          }
        }
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      const offset = 80;
      const elementPosition = element.offsetTop - offset;
      window.scrollTo({
        top: elementPosition,
        behavior: "smooth",
      });
    }
    setIsMenuOpen(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${BACKEND_URL}/api/contact`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(formData),
      });

      const data = await response.json();

      if (response.ok) {
        toast.success(
          data.message ||
            "Message sent successfully! We'll get back to you soon."
        );
        setFormData({ name: "", email: "", company: "", message: "" });
      } else {
        toast.error(data.detail || "Failed to send message. Please try again.");
      }
    } catch (error) {
      console.error("Error sending contact form:", error);
      toast.error(
        "Failed to send message. Please check your connection and try again."
      );
    }
  };

  const services = [
    {
      icon: <Code className="w-8 h-8" />,
      title: "Custom Web Development",
      description:
        "Tailored web solutions built with modern technologies to meet your unique business requirements.",
    },
    {
      icon: <Globe className="w-8 h-8" />,
      title: "Full-Stack Applications",
      description:
        "End-to-end development of scalable applications with robust backend and intuitive frontend.",
    },
    {
      icon: <Zap className="w-8 h-8" />,
      title: "Performance Optimization",
      description:
        "Speed and efficiency at the core. We build fast, responsive applications that users love.",
    },
    {
      icon: <Shield className="w-8 h-8" />,
      title: "Secure & Scalable",
      description:
        "Enterprise-grade security and architecture that grows with your business needs.",
    },
  ];

  const technologies = [
    { name: "React", category: "Frontend" },
    { name: "Next.js", category: "Frontend" },
    { name: "Vue.js", category: "Frontend" },
    { name: "Node.js", category: "Backend" },
    { name: "Python", category: "Backend" },
    { name: "FastAPI", category: "Backend" },
    { name: "PostgreSQL", category: "Database" },
    { name: "MongoDB", category: "Database" },
    { name: "AWS", category: "Cloud" },
    { name: "Docker", category: "DevOps" },
    { name: "Kubernetes", category: "DevOps" },
    { name: "TypeScript", category: "Language" },
  ];

  const careers = [
    {
      title: "Senior Full-Stack Developer",
      location: "Yerevan, Armenia",
      type: "Full-time",
      description:
        "Join our team to build cutting-edge web applications for international clients.",
    },
    {
      title: "UI/UX Designer",
      location: "Yerevan, Armenia / Remote",
      type: "Full-time",
      description: "Create beautiful, intuitive interfaces that users love.",
    },
    {
      title: "DevOps Engineer",
      location: "Yerevan, Armenia",
      type: "Full-time",
      description:
        "Build and maintain robust infrastructure for scalable applications.",
    },
  ];

  const blogPosts = [
    {
      title: "The Future of Web Development in 2025",
      excerpt:
        "Exploring emerging trends and technologies shaping the future of web development.",
      date: "May 15, 2025",
      category: "Technology",
    },
    {
      title: "Building Scalable Applications: Best Practices",
      excerpt:
        "Learn how to architect applications that can handle millions of users.",
      date: "May 10, 2025",
      category: "Development",
    },
    {
      title: "Why Choose Armenia for Tech Outsourcing",
      excerpt:
        "Discover the advantages of partnering with Armenian tech companies.",
      date: "May 5, 2025",
      category: "Business",
    },
  ];

  return (
    <div className="invera-app">
      <header className="fixed top-0 left-0 right-0 z-50 bg-black/80 backdrop-blur-lg border-b border-white/10">
        <div className="container mx-auto px-6 lg:px-12">
          <div className="flex items-center justify-between h-20">
            <div
              className="flex items-center cursor-pointer"
              onClick={() => scrollToSection("home")}
            >
              <img
                src="/images/logoH.png"
                id="logoInveraHeader"
                alt="INVERA"
                className="h-12"
              />
            </div>

            <nav className="hidden md:flex items-center gap-8">
              {[
                "home",
                "about",
                "services",
                "technologies",
                "careers",
                "blog",
                "contact",
              ].map((section) => (
                <button
                  key={section}
                  onClick={() => scrollToSection(section)}
                  className={`text-base font-medium transition-colors capitalize ${
                    activeSection === section
                      ? "text-purple-400"
                      : "text-gray-400 hover:text-white"
                  }`}
                >
                  {section}
                </button>
              ))}
            </nav>

            <Button
              onClick={() => scrollToSection("contact")}
              className="hidden md:flex bg-gradient-to-r from-purple-600 to-blue-500 hover:from-purple-700 hover:to-blue-600 text-white border-0"
            >
              Get in Touch
              <ArrowRight className="ml-2 w-4 h-4" />
            </Button>

            <button
              className="md:hidden text-white"
              onClick={() => setIsMenuOpen(!isMenuOpen)}
            >
              {isMenuOpen ? (
                <X className="w-6 h-6" />
              ) : (
                <Menu className="w-6 h-6" />
              )}
            </button>
          </div>
        </div>

        {isMenuOpen && (
          <div className="md:hidden bg-black/95 backdrop-blur-lg border-t border-white/10">
            <nav className="container mx-auto px-6 py-6 flex flex-col gap-4">
              {[
                "home",
                "about",
                "services",
                "technologies",
                "careers",
                "blog",
                "contact",
              ].map((section) => (
                <button
                  key={section}
                  onClick={() => scrollToSection(section)}
                  className={`text-left text-base font-medium transition-colors capitalize ${
                    activeSection === section
                      ? "text-purple-400"
                      : "text-gray-400 hover:text-white"
                  }`}
                >
                  {section}
                </button>
              ))}
            </nav>
          </div>
        )}
      </header>

      <section
        id="home"
        className="min-h-screen flex items-center justify-center relative overflow-hidden pt-20"
      >
        <div className="absolute inset-0 bg-gradient-to-br from-purple-900/20 via-black to-blue-900/20"></div>
        <div className="absolute inset-0 opacity-20">
          <div
            className="absolute inset-0"
            style={{
              backgroundImage:
                "repeating-linear-gradient(0deg, transparent, transparent 1px, rgba(139, 92, 246, 0.1) 1px, rgba(139, 92, 246, 0.1) 80px), repeating-linear-gradient(-90deg, transparent, transparent 1px, rgba(59, 130, 246, 0.1) 1px, rgba(59, 130, 246, 0.1) 80px)",
            }}
          ></div>
        </div>

        <div className="container mx-auto px-6 lg:px-12 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-5xl md:text-7xl font-bold text-white mb-6 leading-tight">
              Innovate. Connect.{" "}
              <span className="bg-gradient-to-r from-purple-400 to-blue-400 bg-clip-text text-transparent">
                Future.
              </span>
            </h1>
            <p className="text-xl md:text-2xl text-gray-300 mb-8 leading-relaxed">
              INVERA delivers cutting-edge web development solutions for
              businesses across Europe and beyond. We build scalable, secure,
              and modern applications that drive growth.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                onClick={() => scrollToSection("contact")}
                size="lg"
                className="bg-gradient-to-r from-purple-600 to-blue-500 hover:from-purple-700 hover:to-blue-600 text-white text-lg px-8 py-6 border-0"
              >
                Start Your Project
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
              <Button
                onClick={() => scrollToSection("services")}
                size="lg"
                variant="outline"
                className="text-lg px-8 py-6 border-purple-500/50 text-purple-400 hover:bg-purple-500/10 hover:text-purple-300"
              >
                Explore Services
              </Button>
            </div>
          </div>
        </div>

        <div className="absolute bottom-10 left-1/2 transform -translate-x-1/2 animate-bounce">
          <div className="w-6 h-10 border-2 border-purple-400/50 rounded-full flex items-start justify-center p-2">
            <div className="w-1 h-2 bg-purple-400 rounded-full"></div>
          </div>
        </div>
      </section>

      <section
        id="about"
        className="py-24 bg-gradient-to-b from-black to-gray-900"
      >
        <div className="container mx-auto px-6 lg:px-12">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6 text-center">
              About{" "}
              <span className="bg-gradient-to-r from-purple-400 to-blue-400 bg-clip-text text-transparent">
                INVERA
              </span>
            </h2>
            <div className="w-24 h-1 bg-gradient-to-r from-purple-600 to-blue-500 mx-auto mb-12"></div>

            <div className="space-y-6 text-gray-300 text-lg leading-relaxed">
              <p>
                INVERA is a forward-thinking web development company based in
                Armenia, serving clients across Europe and internationally. We
                specialize in creating high-quality, scalable web solutions that
                empower businesses to thrive in the digital age.
              </p>
              <p>
                Our mission is to bridge innovation and business needs through
                cutting-edge technology. We believe in building long-term
                partnerships with our clients, delivering solutions that not
                only meet today's requirements but are also prepared for
                tomorrow's challenges.
              </p>
              <p>
                With a team of experienced developers and designers, we focus on
                quality, reliability, and innovation. Every project we undertake
                reflects our commitment to excellence and our passion for
                creating web experiences that make a difference.
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-8 mt-16">
              <div className="text-center">
                <div className="w-16 h-16 bg-gradient-to-r from-purple-600 to-blue-500 rounded-lg mx-auto mb-4 flex items-center justify-center">
                  <Users className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-2xl font-bold text-white mb-2">50+</h3>
                <p className="text-gray-400">Happy Clients</p>
              </div>
              <div className="text-center">
                <div className="w-16 h-16 bg-gradient-to-r from-purple-600 to-blue-500 rounded-lg mx-auto mb-4 flex items-center justify-center">
                  <Code className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-2xl font-bold text-white mb-2">100+</h3>
                <p className="text-gray-400">Projects Delivered</p>
              </div>
              <div className="text-center">
                <div className="w-16 h-16 bg-gradient-to-r from-purple-600 to-blue-500 rounded-lg mx-auto mb-4 flex items-center justify-center">
                  <Globe className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-2xl font-bold text-white mb-2">15+</h3>
                <p className="text-gray-400">Countries Served</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section id="services" className="py-24 bg-black">
        <div className="container mx-auto px-6 lg:px-12">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6 text-center">
            Our{" "}
            <span className="bg-gradient-to-r from-purple-400 to-blue-400 bg-clip-text text-transparent">
              Services
            </span>
          </h2>
          <div className="w-24 h-1 bg-gradient-to-r from-purple-600 to-blue-500 mx-auto mb-12"></div>
          <p className="text-xl text-gray-400 text-center max-w-3xl mx-auto mb-16">
            We deliver comprehensive web development services tailored to your
            business needs
          </p>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {services.map((service, index) => (
              <Card
                key={index}
                className="bg-gray-900/50 border-purple-500/20 hover:border-purple-500/50 transition-all duration-300 hover:transform hover:scale-105 hover:shadow-xl hover:shadow-purple-500/20"
              >
                <CardHeader>
                  <div className="w-16 h-16 bg-gradient-to-r from-purple-600 to-blue-500 rounded-lg mb-4 flex items-center justify-center text-white">
                    {service.icon}
                  </div>
                  <CardTitle className="text-white text-xl">
                    {service.title}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <CardDescription className="text-gray-400 text-base">
                    {service.description}
                  </CardDescription>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      <section
        id="technologies"
        className="py-24 bg-gradient-to-b from-black to-gray-900"
      >
        <div className="container mx-auto px-6 lg:px-12">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6 text-center">
            <span className="bg-gradient-to-r from-purple-400 to-blue-400 bg-clip-text text-transparent">
              Technologies
            </span>{" "}
            We Use
          </h2>
          <div className="w-24 h-1 bg-gradient-to-r from-purple-600 to-blue-500 mx-auto mb-12"></div>
          <p className="text-xl text-gray-400 text-center max-w-3xl mx-auto mb-16">
            We work with cutting-edge technologies to build modern, performant
            applications
          </p>

          <div className="max-w-5xl mx-auto">
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
              {technologies.map((tech, index) => (
                <div
                  key={index}
                  className="bg-gray-900/50 border border-purple-500/20 rounded-lg p-6 text-center hover:border-purple-500/50 hover:transform hover:scale-105 transition-all duration-300 hover:shadow-lg hover:shadow-purple-500/20"
                >
                  <h3 className="text-white font-semibold text-lg mb-2">
                    {tech.name}
                  </h3>
                  <p className="text-gray-500 text-sm">{tech.category}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section id="careers" className="py-24 bg-black">
        <div className="container mx-auto px-6 lg:px-12">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6 text-center">
            Join Our{" "}
            <span className="bg-gradient-to-r from-purple-400 to-blue-400 bg-clip-text text-transparent">
              Team
            </span>
          </h2>
          <div className="w-24 h-1 bg-gradient-to-r from-purple-600 to-blue-500 mx-auto mb-12"></div>
          <p className="text-xl text-gray-400 text-center max-w-3xl mx-auto mb-16">
            We're always looking for talented individuals who are passionate
            about technology and innovation
          </p>

          <div className="max-w-4xl mx-auto space-y-6">
            {careers.map((job, index) => (
              <Card
                key={index}
                className="bg-gray-900/50 border-purple-500/20 hover:border-purple-500/50 transition-all duration-300 hover:shadow-lg hover:shadow-purple-500/20"
              >
                <CardHeader>
                  <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                    <div>
                      <CardTitle className="text-white text-2xl mb-2">
                        {job.title}
                      </CardTitle>
                      <div className="flex flex-wrap gap-4 text-gray-400">
                        <div className="flex items-center gap-2">
                          <MapPin className="w-4 h-4" />
                          <span>{job.location}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <Briefcase className="w-4 h-4" />
                          <span>{job.type}</span>
                        </div>
                      </div>
                    </div>
                    <Button
                      onClick={() => scrollToSection("contact")}
                      className="bg-gradient-to-r from-purple-600 to-blue-500 hover:from-purple-700 hover:to-blue-600 text-white border-0"
                    >
                      Apply Now
                    </Button>
                  </div>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-400">{job.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      <section
        id="blog"
        className="py-24 bg-gradient-to-b from-black to-gray-900"
      >
        <div className="container mx-auto px-6 lg:px-12">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6 text-center">
            Latest{" "}
            <span className="bg-gradient-to-r from-purple-400 to-blue-400 bg-clip-text text-transparent">
              Insights
            </span>
          </h2>
          <div className="w-24 h-1 bg-gradient-to-r from-purple-600 to-blue-500 mx-auto mb-12"></div>
          <p className="text-xl text-gray-400 text-center max-w-3xl mx-auto mb-16">
            Stay updated with the latest trends, insights, and best practices in
            web development
          </p>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {blogPosts.map((post, index) => (
              <Card
                key={index}
                className="bg-gray-900/50 border-purple-500/20 hover:border-purple-500/50 transition-all duration-300 hover:transform hover:scale-105 hover:shadow-xl hover:shadow-purple-500/20 cursor-pointer"
              >
                <CardHeader>
                  <div className="flex items-center gap-2 text-sm text-gray-500 mb-2">
                    <BookOpen className="w-4 h-4" />
                    <span>{post.category}</span>
                    <span>•</span>
                    <span>{post.date}</span>
                  </div>
                  <CardTitle className="text-white text-xl hover:text-purple-400 transition-colors">
                    {post.title}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-400">{post.excerpt}</p>
                  <Button
                    variant="link"
                    className="text-purple-400 hover:text-purple-300 p-0 mt-4"
                  >
                    Read More <ArrowRight className="ml-2 w-4 h-4" />
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      <section id="contact" className="py-24 bg-black">
        <div className="container mx-auto px-6 lg:px-12">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6 text-center">
            Get in{" "}
            <span className="bg-gradient-to-r from-purple-400 to-blue-400 bg-clip-text text-transparent">
              Touch
            </span>
          </h2>
          <div className="w-24 h-1 bg-gradient-to-r from-purple-600 to-blue-500 mx-auto mb-12"></div>
          <p className="text-xl text-gray-400 text-center max-w-3xl mx-auto mb-16">
            Ready to start your next project? Let's discuss how we can help your
            business grow
          </p>

          <div className="max-w-4xl mx-auto grid md:grid-cols-2 gap-12">
            <div>
              <h3 className="text-2xl font-bold text-white mb-6">
                Contact Information
              </h3>
              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-gradient-to-r from-purple-600 to-blue-500 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Mail className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h4 className="text-white font-semibold mb-1">Email</h4>
                    <a
                      href="mailto:apoghosyan250493@gmail.com"
                      className="text-gray-400 hover:text-purple-400 transition-colors"
                    >
                      apoghosyan250493@gmail.com
                    </a>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-gradient-to-r from-purple-600 to-blue-500 rounded-lg flex items-center justify-center flex-shrink-0">
                    <MapPin className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h4 className="text-white font-semibold mb-1">Location</h4>
                    <p className="text-gray-400">Yerevan, Armenia</p>
                  </div>
                </div>
              </div>

              <div className="mt-12">
                <h4 className="text-white font-semibold mb-4">Follow Us</h4>
                <div className="flex gap-4">
                  <a
                    href="#"
                    className="w-10 h-10 bg-gray-800 hover:bg-gradient-to-r hover:from-purple-600 hover:to-blue-500 rounded-lg flex items-center justify-center transition-all duration-300"
                  >
                    <Linkedin className="w-5 h-5 text-white" />
                  </a>
                  <a
                    href="#"
                    className="w-10 h-10 bg-gray-800 hover:bg-gradient-to-r hover:from-purple-600 hover:to-blue-500 rounded-lg flex items-center justify-center transition-all duration-300"
                  >
                    <Twitter className="w-5 h-5 text-white" />
                  </a>
                  <a
                    href="#"
                    className="w-10 h-10 bg-gray-800 hover:bg-gradient-to-r hover:from-purple-600 hover:to-blue-500 rounded-lg flex items-center justify-center transition-all duration-300"
                  >
                    <Github className="w-5 h-5 text-white" />
                  </a>
                </div>
              </div>
            </div>

            <div>
              <Card className="bg-gray-900/50 border-purple-500/20">
                <CardHeader>
                  <CardTitle className="text-white text-2xl">
                    Send us a message
                  </CardTitle>
                  <CardDescription className="text-gray-400">
                    Fill out the form below and we'll get back to you as soon as
                    possible
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <form onSubmit={handleSubmit} className="space-y-4">
                    <div>
                      <Input
                        placeholder="Your Name"
                        value={formData.name}
                        onChange={(e) =>
                          setFormData({ ...formData, name: e.target.value })
                        }
                        required
                        className="bg-gray-800 border-gray-700 text-white placeholder:text-gray-500 focus:border-purple-500"
                      />
                    </div>
                    <div>
                      <Input
                        type="email"
                        placeholder="Your Email"
                        value={formData.email}
                        onChange={(e) =>
                          setFormData({ ...formData, email: e.target.value })
                        }
                        required
                        className="bg-gray-800 border-gray-700 text-white placeholder:text-gray-500 focus:border-purple-500"
                      />
                    </div>
                    <div>
                      <Input
                        placeholder="Company (Optional)"
                        value={formData.company}
                        onChange={(e) =>
                          setFormData({ ...formData, company: e.target.value })
                        }
                        className="bg-gray-800 border-gray-700 text-white placeholder:text-gray-500 focus:border-purple-500"
                      />
                    </div>
                    <div>
                      <Textarea
                        placeholder="Your Message"
                        value={formData.message}
                        onChange={(e) =>
                          setFormData({ ...formData, message: e.target.value })
                        }
                        required
                        rows={5}
                        className="bg-gray-800 border-gray-700 text-white placeholder:text-gray-500 focus:border-purple-500 resize-none"
                      />
                    </div>
                    <Button
                      type="submit"
                      className="w-full bg-gradient-to-r from-purple-600 to-blue-500 hover:from-purple-700 hover:to-blue-600 text-white border-0"
                    >
                      Send Message
                      <ArrowRight className="ml-2 w-4 h-4" />
                    </Button>
                  </form>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      <footer className="bg-gray-900 border-t border-white/10 py-12">
        <div className="container mx-auto px-6 lg:px-12">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="flex flex-wrap justify-center gap-6 text-gray-400">
              <button
                onClick={() => scrollToSection("about")}
                className="hover:text-purple-400 transition-colors"
              >
                About
              </button>
              <button
                onClick={() => scrollToSection("services")}
                className="hover:text-purple-400 transition-colors"
              >
                Services
              </button>
              <button
                onClick={() => scrollToSection("careers")}
                className="hover:text-purple-400 transition-colors"
              >
                Careers
              </button>
              <button
                onClick={() => scrollToSection("blog")}
                className="hover:text-purple-400 transition-colors"
              >
                Blog
              </button>
              <button
                onClick={() => scrollToSection("contact")}
                className="hover:text-purple-400 transition-colors"
              >
                Contact
              </button>
            </div>
            <div className="text-gray-400 text-sm">
              © 2025 INVERA. All rights reserved.
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Home;
